import {
    AnyTask,
    ExportCompletedContent,
    ExportPaymentCompletedContent,
    MercureMessage,
    Point,
    TriggerKpiProgressContent,
    TriggerPromoProcessContent,
} from "tm-shared/types";

export enum ModalType {
    Promos = "promos",
    UncompletedCheckouts = "uncompleted-checkouts",
    UncompletedTasks = "uncompleted-tasks",
    KpiProgress = "kpi-progress",
    Messages = "messages",
    Exports = "exports",
    State = "state",
    UnscheduledPointCompletion = "uncheduled-point-completion",
    CreateCustomTask = "createCustomTask",
    CustomTaskAddPhotos = "customTaskAddPhotos",
    YesterdayOpenedCheckouts = "yesterdayOpenedCheckouts",
    UploadRetry = "uploadRetry",
    ExportPayment = "exportPayment",
}

interface AsyncBase {
    deliveryId: string;
}

export type ModalPayloads = {
    [ModalType.UploadRetry]: { initialTab?: "photo" | "request" };

    [ModalType.Promos]: AsyncBase & {
        promoInfo: TriggerPromoProcessContent;
    };

    [ModalType.Messages]: MercureMessage;

    [ModalType.KpiProgress]: AsyncBase & TriggerKpiProgressContent;

    [ModalType.Exports]: AsyncBase & ExportCompletedContent;

    [ModalType.ExportPayment]: AsyncBase & ExportPaymentCompletedContent;

    [ModalType.State]: AsyncBase;

    [ModalType.UnscheduledPointCompletion]: AsyncBase & {
        pointsCount: number;
    };

    [ModalType.CustomTaskAddPhotos]: {
        point: Point;
        task: AnyTask;
        images: string[];
    };
    [ModalType.CreateCustomTask]: {
        point?: Point;
        agentId?: string;
        routeId?: string;
        images: string[];
    };
};

export type ModalState = { id: string } & (
    | {
          type: ModalType.Promos;
          payload: ModalPayloads[ModalType.Promos];
      }
    | {
          type: ModalType.UploadRetry;
          payload: ModalPayloads[ModalType.UploadRetry];
      }
    | {
          type: ModalType.UncompletedCheckouts;
      }
    | {
          type: ModalType.UncompletedTasks;
      }
    | {
          type: ModalType.KpiProgress;
          payload: ModalPayloads[ModalType.KpiProgress];
      }
    | {
          type: ModalType.Messages;
          payload: ModalPayloads[ModalType.Messages];
      }
    | {
          type: ModalType.Exports;
          payload: ModalPayloads[ModalType.Exports];
      }
    | {
          type: ModalType.ExportPayment;
          payload: ModalPayloads[ModalType.ExportPayment];
      }
    | {
          type: ModalType.State;
          payload: ModalPayloads[ModalType.State];
      }
    | {
          type: ModalType.UnscheduledPointCompletion;
          payload: ModalPayloads[ModalType.UnscheduledPointCompletion];
      }
    | {
          type: ModalType.CustomTaskAddPhotos;
          payload: ModalPayloads[ModalType.CustomTaskAddPhotos];
      }
    | {
          type: ModalType.CreateCustomTask;
          payload: ModalPayloads[ModalType.CreateCustomTask];
      }
    | {
          type: ModalType.YesterdayOpenedCheckouts;
      }
    | null
);

// ---------------------- Point Create Page ---------------------

export enum PointCreateModal {
    AddAddress = "addAddress",
    AddContragent = "addContragent",
    AddIllegalContragent = "addIllegalContragent",
    DuplicateByAddress = "duplicateByAddress",
    DuplicateById = "duplicateById",
    AddNetwork = "addNetwork",
}

export interface PointCreateModalPayloads {
    [PointCreateModal.DuplicateById]: {
        point: Point;
        routeCount: number;
    };
}

export type PointCreateModalState =
    | {
          type: PointCreateModal.AddAddress;
          payload: AddAddressModalPayload;
      }
    | {
          type: PointCreateModal.AddContragent;
      }
    | {
          type: PointCreateModal.DuplicateByAddress;
          payload: DuplicateByAddressModalPayload;
      }
    | {
          type: PointCreateModal.DuplicateById;
          payload: PointCreateModalPayloads[PointCreateModal.DuplicateById];
      }
    | {
          type: PointCreateModal.AddNetwork;
      }
    | {
          type: PointCreateModal.AddIllegalContragent;
      };

//  ------------------------- Shared Modals --------------------

export interface AddAddressModalPayload {
    region: string;
    regionId: string;
    variant?: "createPoint" | "verifyAddress";
}

export interface DuplicateByAddressModalPayload {
    points: Point[];
    address: string;
    variant?: "createPoint" | "verifyAddress";
}
